<template>
  <section>
    <div class="home-cash">
      <FadeTransition>
        <div v-if="!hasError" class="home-cash__balance-container">
          <HomeCashSummary
            :is-loading="isLoading"
            :balance="balance"
            :processing-warning-text="processingWarningText"
            @click="redirectToTransactionsPage"
          />
        </div>
      </FadeTransition>
      <HomeCashActions
        :is-overdue="isOverdue"
        :loading="isLoading"
        :has-positive-balance="Boolean(hasPositiveBalance)"
        :cash-account-id="cashAccountId"
        :disable-actions="hasError || !hasActiveAccount"
        data-testid="home-cash-actions"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { ErrorHandler } from '@/modules/common/components/ErrorHandler';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import HomeCashActions from './components/HomeCashActions.vue';
import HomeCashSummary from './components/HomeCashSummary.vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  trackReloadBalanceAccountErrorClick,
  trackBalanceAccountErrorLoad
} from './track-events';
import { trackTransactionsPageNavigation } from '@/modules/transaction/helpers/transactions-events';
import { WarrenModules } from '@/types/navigation';
import * as trackEvents from './track-events';

import {
  getCashSummary,
  CashSummaryResponse
} from '@/modules/home/services/bff';

@Component({
  components: {
    FadeTransition,
    HomeCashActions,
    ErrorHandler,
    HomeCashSummary
  }
})
export default class HomeCash extends Vue {
  readonly NebraskaColors = NebraskaColors;

  @Prop({ type: Boolean, required: true })
  readonly isOverdue!: boolean;

  @Prop({ type: Boolean, required: false })
  public retry!: boolean;

  newCashData: Reactive<CashSummaryResponse> = null;
  isLoading = false;
  hasError = false;
  showBalanceDetail = false;

  created() {
    this.fetchNewCash();
  }

  @Watch('retry', { deep: true })
  watchReloadCash() {
    this.fetchNewCash();
    trackReloadBalanceAccountErrorClick();
  }

  async fetchNewCash() {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.newCashData = await getCashSummary();
    } catch {
      this.hasError = true;
      this.$emit('on-error', this.hasError);
      trackBalanceAccountErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  get balance() {
    return this.cashSectionSummary.accountAmount;
  }

  get cashSectionSummary() {
    return {
      netBalance: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.netBalance?.formatted,
        ''
      ),
      accountAmount: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.accountAmount?.formatted,
        ''
      )
    };
  }

  getValueWithDefault(value?: string | null, defaultValue = 'R$ 0,00') {
    if (value) return value;
    return defaultValue;
  }

  get hasPositiveBalance() {
    return (
      this.newCashData?.cashSection.summary?.netBalance?.value &&
      this.newCashData.cashSection.summary.netBalance.value > 0
    );
  }

  get hasActiveAccount(): boolean {
    return Boolean(
      this.newCashData?.cashSection.status?.hasInvestmentAccountEnabled
    );
  }

  get processingWarningText() {
    return this.newCashData?.cashSection.status?.processingWarning;
  }

  get cashAccountId() {
    return this.newCashData?.cashSection.status?.investmentAccountId ?? '';
  }

  public redirectToTransactionsPage() {
    this.$router.push({ name: 'transactions' });

    trackTransactionsPageNavigation(WarrenModules.Home);
    trackEvents.trackViewMoreTransactionsEvent();
  }
}
</script>

<style lang="less" scoped>
@max-width: 1008px;

.home-cash {
  max-width: @max-width;
  margin: @size-spacing-x1000 auto;
  gap: @size-spacing-x300;
  display: flex;

  &__balance-container {
    display: flex;
    align-items: center;

    .transit-loader {
      margin-left: @size-spacing-x900;
      &__title {
        width: 107px;
        height: 20px;
        border-radius: @size-radius-x100;
        margin-bottom: @size-spacing-x150;
      }
      &__total {
        width: 107px;
        height: 30px;
        border-radius: @size-radius-x100;
      }
    }

    .heading-display {
      margin-left: @size-spacing-x900;
    }

    .balance-transit {
      display: flex;
      align-items: center;
    }
  }

  .to-lg({
    flex-flow: column;
    align-items: flex-start;

    &__balance-container {
      width: 100%;
      margin-bottom: @size-spacing-x700;
    }
  });

  &--error {
    display: flex;
    width: 100%;
    justify-content: space-between;
    border-bottom: @size-border-x400 solid @divider-primary;

    .to-sm({
      flex-direction: column;
    });
  }
}
</style>
